import React, { useEffect, useRef, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { User } from '../../../../../../model/user/User';
import { CustomImageHandler } from '../../../../../../shared/components/CustomImageHandler';
import MentionInputField from '../../../../../../shared/components/message/MentionInputField';
import { ManageService } from '../../../manage/service';
import { BriefingService } from '../../service';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { IParams } from '../../../../../../model/shared/IParams';
import { ErrorToast, WarningToast } from '../../../../../../utils/toasters';
import {
    formatDate,
    getFirstPreviewImage,
    getStatusIcon,
    hasAccessRights,
} from '../../../../../../shared/functions/Functions';
import { ICampaignOverview } from '../../../../../../model/briefing/overview/ICampaignOverview';
import Tooltip from '@mui/material/Tooltip';
import { priceSplitter } from '../../../../../../utils/helpers/priceSplitter';
import { FaCog } from 'react-icons/fa';
import { MessageBody } from '../../../../../../shared/components/message/MessageBody';
import { iManageMessage } from '../../../manage/calendar/dto/IManageCalendar';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../store/rootReducer';
import { MdSend } from 'react-icons/md';
import { IBriefingCommentRequest } from '../../dto/IBriefingCommentRequest';
import { AccessRightEnum } from '../../../../../../enum/AccessRightEnum';
import { FindCreatorsInfo } from './FindCreatorsInfo';
import HtmlToPdf from '../../../../../../shared/components/generate-pdf';
import { LinkedCampaignsCard } from './LinkedCampaignsCard';
import LinkToPublicCard from './LinkToPublicCard';
import { PlannerStatusEnum } from '../../../../../../enum/PlannerStatusEnum';
import { CampaignStatusEnum } from '../../../../../../enum/CampaignStatusEnum';
import CardLayout from '../../../../../../shared/components/card/CardLayout';
import {
    CampaignOverviewCardSkeleton,
    LinkedCampaignsSectionSkeleton,
    LinkToPublicCardSkeleton,
} from './BriefingOverviewSkeletons';
import { MessageListSkeleton } from '../../../../../../shared/components/message/MessageListSkeleton';

interface ICampaignOverviewCardProps {
    triggerUpdate?: boolean;
    openEditCampaignModal?: (editContractType?: boolean) => void;
}

export function CampaignOverviewCard({ triggerUpdate, openEditCampaignModal }: ICampaignOverviewCardProps) {
    const { id } = useParams<keyof IParams>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const user = useSelector((states: IAllStates) => states.auth.user);

    const [currentInformation, setCurrentInformation] = useState<ICampaignOverview>();
    const [comments, setComments] = useState<iManageMessage[]>([]);
    const [customerSuccessSuggestions, setCustomerSuccessSuggestions] = useState<User[]>([]);
    const [briefingSuggestions, setBriefingSuggestions] = useState<User[]>([]);
    const [briefingComments, setBriefingComments] = useState<iManageMessage[]>([]);
    const [isSendDisabled, setIsSendDisabled] = useState(false);
    const [newMessage, setNewMessage] = useState<IBriefingCommentRequest>({
        message: '',
    });
    const [newBriefingMessage, setNewBriefingMessage] = useState<IBriefingCommentRequest>({
        message: '',
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isCommentsLoading, setIsCommentsLoading] = useState(true);
    const [isBriefingCommentsLoading, setIsBriefingCommentsLoading] = useState(true);

    const lastMessageRef = useRef<HTMLInputElement>(null);
    const lastBriefingMessageRef = useRef<HTMLInputElement>(null);
    const canSeeBriefingFeedback = user?.accessRights.includes(AccessRightEnum.CAN_VIEW_BRIEFING_CHAT);
    const influencerFeedbackHide = user?.accessRights.includes(AccessRightEnum.INFLUENCER_FEEDBACK_HIDE);

    const goToOverview = (selectedId?: string) => {
        navigate(`/brand/create/campaign/${selectedId ?? id}/overview`);
    };

    useEffect(() => {
        if (!+id!) {
            return;
        }
        BriefingService.getBriefingByStep(id ?? '', 'overview')
            .then(response => {
                if (response?.isDeleted) {
                    goToOverview();
                }
                setCurrentInformation(response);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsLoading(false));
    }, [location?.pathname, id, triggerUpdate]);

    useEffect(() => {
        BriefingService.getCustomerSuccessComment(id ?? '', { perPage: 0 })
            .then(response => {
                setComments(response?.data);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsCommentsLoading(false));
    }, [id]);

    useEffect(() => {
        BriefingService.getBriefingComment(id ?? '', { perPage: 0 })
            .then(response => {
                setBriefingComments(response?.data);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsBriefingCommentsLoading(false));
    }, [id]);

    useEffect(() => {
        if (comments?.length && lastMessageRef?.current) {
            lastMessageRef?.current?.scrollIntoView({
                behavior: 'auto',
                block: 'nearest',
            });
        }
    }, [comments?.length, lastMessageRef?.current]);

    useEffect(() => {
        if (briefingComments?.length && lastBriefingMessageRef?.current) {
            lastBriefingMessageRef?.current?.scrollIntoView({
                behavior: 'auto',
                block: 'nearest',
            });
        }
    }, [briefingComments?.length, lastBriefingMessageRef?.current]);

    useEffect(() => {
        if (!!currentInformation?.id) {
            ManageService.getMentionParticipants(currentInformation?.id, false)
                .then(response => {
                    setCustomerSuccessSuggestions(response?.data);
                })
                .catch(err => ErrorToast(err));
            ManageService.getMentionParticipants(currentInformation?.id, true, true)
                .then(response => {
                    setBriefingSuggestions(response?.data);
                })
                .catch(err => ErrorToast(err));
        }
    }, [currentInformation?.id]);

    const sendMessageHandler = () => {
        if (!newMessage?.message?.trim()?.length) {
            return WarningToast(t('general.toasts.warnEmptyMessage'));
        }
        setIsSendDisabled(true);
        BriefingService.postCustomerSuccessComment({
            ...newMessage,
            campaignId: id,
            message: newMessage.message.replace(/\n/g, '</br>'),
        })
            .then(response => {
                setComments(prevState => prevState.concat(response));
                setNewMessage({ ...newMessage, message: '' });
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsSendDisabled(false));
    };

    const sendBriefingMessageHandler = () => {
        if (!newBriefingMessage?.message?.trim()?.length) {
            return WarningToast(t('general.toasts.warnEmptyMessage'));
        }
        setIsSendDisabled(true);
        BriefingService.postBriefingComment({
            ...newBriefingMessage,
            campaignId: id,
            message: newBriefingMessage.message.replace(/\n/g, '</br>'),
        })
            .then(response => {
                setNewBriefingMessage({ ...newBriefingMessage, message: '' });
                setBriefingComments(prevState => prevState.concat(response));
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsSendDisabled(false));
    };

    const sendBriefingMessagePressingEnter = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === 'Enter') {
            return sendBriefingMessageHandler();
        }
    };

    const changeMessageHandler = (name: string, value: string) => {
        setNewMessage({ ...newMessage, [name]: value });
    };

    const changeHandlerBriefing = (name: string, value: string) => {
        setNewBriefingMessage({ ...newBriefingMessage, [name]: value });
    };

    return (
        <div className="position-relative">
            {isLoading ? (
                <>
                    <CampaignOverviewCardSkeleton />
                    <LinkedCampaignsSectionSkeleton />
                    <LinkToPublicCardSkeleton />
                </>
            ) : (
                <>
                    {currentInformation?.status && (
                        <div className={`offer-status-badge mt-4 ${currentInformation?.status} z-index-2`}>
                            {getStatusIcon(currentInformation?.status ?? '', 'rotate--120 text-white')}
                            <p>{t(`general.campaign_status.${currentInformation?.status}`)}</p>
                        </div>
                    )}
                    <Card className="mb-4 mt-4 mt-md-0 overflow-hidden">
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    {getFirstPreviewImage('style_uploaded_image', currentInformation?.campaignImages)}
                                    {currentInformation?.approvalStatus && (
                                        <span
                                            className={`d-md-none place-top-right status w-max-content ${currentInformation?.approvalStatus}`}
                                        >
                                            {currentInformation?.approvalStatus
                                                ? currentInformation?.approvalStatus
                                                : '-'}
                                        </span>
                                    )}
                                </Col>
                                <Col md={6} className="d-flex flex-column align-items-md-start justify-content-between">
                                    <div className="mt-3 mt-md-0 d-flex align-items-center justify-content-between overflow-auto w-100">
                                        <h5 className="mb-0 font-weight-bold pr-3">
                                            {currentInformation?.title ? currentInformation?.title : '-'}
                                        </h5>
                                        {!!openEditCampaignModal && (
                                            <FaCog
                                                className="cursor-pointer"
                                                style={{ right: 0, minWidth: 23 }}
                                                id="edit-icon"
                                                name="modal"
                                                onClick={() =>
                                                    openEditCampaignModal &&
                                                    !currentInformation?.isDeleted &&
                                                    openEditCampaignModal()
                                                }
                                            />
                                        )}
                                    </div>
                                    {currentInformation?.approvalStatus && (
                                        <span
                                            className={`mt-3 status w-max-content ${currentInformation?.approvalStatus} d-none d-md-block`}
                                        >
                                            {currentInformation?.approvalStatus
                                                ? currentInformation?.approvalStatus
                                                : '-'}
                                        </span>
                                    )}
                                    {currentInformation?.isLongTerm ? (
                                        <div className="d-flex align-items-end mb-3 mb-md-0 d-md-block mt-3 text-center text-md-start">
                                            <div className="col-6 col-md-12 pl-0 text-center text-md-start">
                                                <p className="mb-0 text-primary font-weight-semi-bold">
                                                    {currentInformation?.date
                                                        ? formatDate(currentInformation?.date as string)
                                                        : 'DD.MM.YYYY.'}
                                                </p>
                                                <p className="mb-0"> {t('brand.campaign.briefing.step3.start_date')}</p>
                                            </div>
                                            <div className="col-6 col-md-12 pl-md-0 pr-0 mt-md-3 text-center text-md-start">
                                                <p className="mb-0 text-primary font-weight-semi-bold">
                                                    {!!currentInformation?.invoiceDuration
                                                        ? t(
                                                              `brand.campaign.briefing.step3.campaign_duration_enum.${currentInformation?.invoiceDuration}`
                                                          )
                                                        : '-'}
                                                </p>
                                                <p className="mb-0">
                                                    {t('brand.campaign.briefing.step3.campaign_duration')}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-end mb-3 mb-md-0 d-md-block mt-3 text-center text-md-start">
                                            <div className="col-6 col-md-12 pl-0 text-center text-md-start">
                                                <p className="mb-0 text-primary font-weight-semi-bold">
                                                    {currentInformation?.date
                                                        ? formatDate(currentInformation?.date as string)
                                                        : 'DD.MM.YYYY.'}
                                                </p>
                                                <p className="mb-0">
                                                    {' '}
                                                    {t('brand.campaign.briefing.application_deadline')}
                                                </p>
                                            </div>
                                            <div className="col-6 col-md-12 pl-md-0 pr-0 mt-md-3 text-center text-md-start">
                                                <p className="mb-0 text-primary font-weight-semi-bold">
                                                    {currentInformation?.publishFrom
                                                        ? formatDate(currentInformation?.publishFrom as string)
                                                        : 'DD.MM.YYYY.'}{' '}
                                                    -
                                                    {currentInformation?.publishTo
                                                        ? formatDate(currentInformation?.publishTo as string)
                                                        : ' DD.MM.YYYY.'}
                                                </p>
                                                <p className="mb-0">
                                                    {t('brand.campaign.briefing.publication_period')}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </Col>

                                <Row className="align-items-end mt-md-4" style={{ rowGap: '10px' }}>
                                    <Col xs={6} md={4} className="px-md-0 text-center">
                                        <h6 className="font-weight-bold mb-0 text-primary">
                                            {currentInformation?.campaignGoal?.title
                                                ? currentInformation?.campaignGoal?.title
                                                : '-'}
                                        </h6>
                                        <p className="my-2">{t('brand.campaign.briefing.campaign_goal')}</p>
                                    </Col>
                                    <Col xs={6} md={4} className="px-md-0 text-center">
                                        <h6 className="font-weight-bold mb-0 text-primary">
                                            {currentInformation?.creatorType?.title
                                                ? currentInformation?.creatorType?.title
                                                : '-'}
                                        </h6>
                                        <p className="my-2 text-center">
                                            {t('brand.campaign.briefing.overview_card.content_creator')}
                                        </p>
                                    </Col>
                                    <Col className="px-md-0 text-center">
                                        {currentInformation?.isCharity ||
                                        !hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) ? (
                                            <p className="my-2"></p>
                                        ) : (
                                            <>
                                                <h6 className="font-weight-bold mb-0 euro-symbol text-primary">
                                                    {priceSplitter(
                                                        Math.round(
                                                            currentInformation?.creatorType?.budgetRanges?.[0]
                                                                ?.recommendedMinBudget ?? 0
                                                        )
                                                    )}
                                                    -{' '}
                                                    {priceSplitter(
                                                        Math.round(
                                                            currentInformation?.creatorType?.budgetRanges?.[0]
                                                                ?.recommendedMaxBudget ?? 0
                                                        )
                                                    )}
                                                </h6>
                                                <p className="my-2 text-center">
                                                    {t('brand.campaign.briefing.overview_card.budget_range')}
                                                </p>
                                            </>
                                        )}
                                    </Col>

                                    <Col
                                        xs={6}
                                        md={4}
                                        className="my-md-2 px-md-0 d-flex flex-column align-items-center"
                                    >
                                        <div className="d-flex align-items-center">
                                            {currentInformation?.mainPlatforms?.length
                                                ? currentInformation?.mainPlatforms?.slice(0, 2)?.map(platform => (
                                                      <div className="mx-1" key={platform?.id}>
                                                          <CustomImageHandler
                                                              photoPath={platform?.path as string}
                                                              altTag={platform?.name ?? 'Social platform image'}
                                                          />
                                                      </div>
                                                  ))
                                                : '-'}
                                            {(currentInformation?.mainPlatforms?.length ?? 0) > 2 && (
                                                <Tooltip
                                                    id="white-tooltip"
                                                    title={
                                                        <>
                                                            {currentInformation?.mainPlatforms
                                                                ?.slice(2)
                                                                ?.map(platform => (
                                                                    <div className="mx-1 my-2" key={platform?.id}>
                                                                        <CustomImageHandler
                                                                            photoPath={platform?.path as string}
                                                                            altTag={
                                                                                platform?.name ??
                                                                                'Social platform image'
                                                                            }
                                                                        />
                                                                    </div>
                                                                ))}
                                                        </>
                                                    }
                                                    placement="right"
                                                    className="cursor-pointer"
                                                >
                                                    <span
                                                        className="icon-round bg-dark font-weight-semi-bold"
                                                        style={{ width: 33 }}
                                                    >
                                                        + {(currentInformation?.mainPlatforms?.length ?? 0) - 2}
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <p className="my-2 text-center">
                                            {t('brand.campaign.briefing.overview_card.platforms')}
                                        </p>
                                    </Col>
                                    <Col xs={6} md={4} className="my-2 px-md-0 text-center">
                                        <h6 className="font-weight-bold mb-0 text-primary">150k-350k</h6>
                                        <p className="my-2 text-center">
                                            {t('brand.campaign.briefing.overview_card.estimated_reach')}
                                        </p>
                                    </Col>
                                    <Col xs={6} md={4} className="mt-0 mb-2 px-md-0 text-center">
                                        <h6 className="font-weight-bold mb-0 d-flex flex-column text-center text-primary">
                                            {currentInformation?.brandedContent?.id ? 'Branded Content' : ''}
                                            {!currentInformation?.brandedContent?.id &&
                                            !currentInformation?.usageRights?.id
                                                ? '-'
                                                : ''}
                                            <br />
                                            {currentInformation?.usageRights?.id ? 'Usage rights' : ''}
                                        </h6>
                                        <p className="my-2 text-center">
                                            {t('brand.campaign.briefing.overview_card.legal')}
                                        </p>
                                    </Col>
                                </Row>
                                {currentInformation?.status !== CampaignStatusEnum.DRAFT && (
                                    <HtmlToPdf styleContent="w-100" isBriefing />
                                )}
                            </Row>
                            {(currentInformation?.isLongTerm || !!currentInformation?.parent?.id) && (
                                <div className="diagonal-text">
                                    <span>
                                        {t(
                                            `brand.campaign.briefing.overview_card.${
                                                currentInformation?.isLongTerm ? 'long_term' : 'linked'
                                            }`
                                        )}
                                    </span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {!!currentInformation?.parent?.id && (
                        <div className="mb-4 mt-4 mt-md-0">
                            <h6>{t('brand.campaign.briefing.overview_card.long_term_campaign')}</h6>
                            <hr className="default-border-lighter" />
                            <LinkedCampaignsCard
                                key={currentInformation?.parent?.id}
                                campaign={currentInformation?.parent}
                                openBriefingAction={goToOverview}
                            />
                        </div>
                    )}
                    {!!currentInformation?.children?.length && (
                        <div className="mb-4 mt-4 mt-md-0">
                            <h6>{t('brand.campaign.briefing.overview_card.linked_campaigns')}</h6>
                            <hr className="default-border-lighter" />
                            <div style={{ maxHeight: '400px' }} className="overflow-auto">
                                {currentInformation?.children?.map((campaign, index) => (
                                    <LinkedCampaignsCard
                                        key={campaign.id + index}
                                        campaign={campaign}
                                        openBriefingAction={goToOverview}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {currentInformation?.hasSupervisor && !influencerFeedbackHide && (
                <CardLayout classCard="mb-4 mt-4 mt-md-0">
                    <h5 className="mb-4 border-bottom pb-2">Customer Success Feedback</h5>
                    <div style={{ maxHeight: '300px' }} className="overflow-auto pr-2">
                        {isCommentsLoading ? (
                            <MessageListSkeleton />
                        ) : !!comments?.length ? (
                            comments.map(comment => (
                                <MessageBody
                                    comment={comment as any}
                                    key={comment?.id}
                                    haveFiles={false}
                                    senderData={comment?.sentBy}
                                    isMyMessage={+comment?.sentBy?.id === +user?.id}
                                />
                            ))
                        ) : (
                            <p>{t('influencer.planner.noComments')}</p>
                        )}
                        <span ref={lastMessageRef} />
                    </div>
                    <div className="border-top d-flex align-items-center pb-2 mt-2 pt-2 w-100">
                        <CustomImageHandler
                            altTag="My profile logo"
                            thumbnailPath={user?.profilePhoto?.thumbnail}
                            photoPath={user?.profilePhoto?.path || user?.profilePhoto?.thumbnail}
                            classes="user-img rounded-circle user-img-rounded"
                            name={user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                        />
                        <div className="w-90 ml-1">
                            <MentionInputField
                                value={newMessage?.message ?? ''}
                                possibleMentions={customerSuccessSuggestions}
                                changeMessageHandler={e => {
                                    changeMessageHandler('message', e.target.value);
                                }}
                                sendMessage={sendMessageHandler}
                            />
                        </div>
                        <MdSend
                            className={`chat-input-icon ml-2 send ${isSendDisabled ? 'opacity-25' : ''}`}
                            onClick={() => {
                                !isSendDisabled && sendMessageHandler();
                            }}
                        />
                    </div>
                </CardLayout>
            )}

            {!isLoading && currentInformation?.approvalStatus === PlannerStatusEnum.APPROVED && (
                <LinkToPublicCard uuid={currentInformation.uuid} route="campaign" />
            )}

            {canSeeBriefingFeedback && (
                <CardLayout classCard="mb-3 mt-4">
                    <h5 className="mb-4 border-bottom pb-2">Briefing Feedback</h5>
                    <div style={{ maxHeight: '300px' }} className="overflow-auto pr-2">
                        {isBriefingCommentsLoading ? (
                            <MessageListSkeleton />
                        ) : !!briefingComments?.length ? (
                            briefingComments.map(comment => (
                                <MessageBody
                                    comment={comment as any}
                                    key={comment?.id}
                                    haveFiles={false}
                                    senderData={comment?.sentBy}
                                    isMyMessage={+comment?.sentBy?.id === +user?.id}
                                />
                            ))
                        ) : (
                            <p>{t('influencer.planner.noComments')}</p>
                        )}
                        <span ref={lastBriefingMessageRef} />
                    </div>
                    <div className="border-top d-flex align-items-center pb-2 mt-2 pt-2 w-100">
                        <CustomImageHandler
                            altTag="My profile logo"
                            thumbnailPath={user?.profilePhoto?.thumbnail}
                            photoPath={user?.profilePhoto?.path || user?.profilePhoto?.thumbnail}
                            classes="user-img rounded-circle user-img-rounded"
                            name={user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                        />
                        <div className="w-90 ml-1 overflow-x-auto pt-2">
                            <MentionInputField
                                value={newBriefingMessage?.message ?? ''}
                                possibleMentions={briefingSuggestions}
                                changeMessageHandler={e => {
                                    changeHandlerBriefing('message', e.target.value);
                                }}
                                sendMessage={sendBriefingMessagePressingEnter}
                            />
                        </div>
                        <MdSend
                            className={`chat-input-icon ml-2 send ${isSendDisabled ? 'opacity-25' : ''}`}
                            onClick={() => {
                                !isSendDisabled && sendBriefingMessageHandler();
                            }}
                        />
                    </div>
                </CardLayout>
            )}

            <div className="d-none d-md-block mt-4">
                <FindCreatorsInfo />
            </div>
        </div>
    );
}
